import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, AlertTriangle, ArrowRight, ArrowLeft, KeyRound } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../hooks/useAuth';
import PasswordInput from '../components/common/PasswordInput';

interface SetupFormData {
  username: string;
  password: string;
  confirmPassword: string;
}

interface FormErrors {
  username?: string;
  password?: string;
  confirmPassword?: string;
  general?: string;
}

export default function Setup() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<SetupFormData>({
    username: '',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState(1);

  const validateStep1 = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    } else if (!/^[a-zA-Z0-9_]+$/.test(formData.username)) {
      newErrors.username = 'Username can only contain letters, numbers, and underscores';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 12) {
      newErrors.password = 'Password must be at least 12 characters';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const { login } = useAuth(); // Add useAuth hook

  const handleNext = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    }
  };

  const handleBack = () => {
    setStep(1);
    setErrors({});
  };

  const submitSetup = async () => {
    if (!validateStep2()) return;

    setIsSubmitting(true);
    setErrors({});

    try {
      await api.post('/auth/setup', {
        username: formData.username,
        password: formData.password,
      });

      // Auto-login after successful setup
      const loginResult = await login(formData.username, formData.password);
      
      if (loginResult.success) {
        navigate('/', { replace: true });
      } else {
        // Fallback to login page if auto-login fails
        navigate('/login', {
          state: { message: 'Account created successfully. Please log in.' },
        });
      }
    } catch (error: any) {
      setErrors({
        general: error.message || 'Setup failed. Please try again.',
      });
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (step === 1) {
      handleNext();
    } else {
      await submitSetup();
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6 relative overflow-hidden">
      {/* Background accents */}
      <div className="absolute inset-0">
        <div className="absolute -top-40 -left-40 h-96 w-96 rounded-full bg-primary/20 blur-3xl" />
        <div className="absolute -bottom-40 -right-40 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
      </div>

      <div className="relative w-full max-w-lg">
        {/* Branding */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10 border border-primary/20 text-primary mb-4 shadow-glow">
            <Shield className="w-7 h-7" />
          </div>
          <h1 className="text-3xl font-bold text-text-header tracking-tight">Welcome to Budget</h1>
          <p className="mt-2 text-text-muted">Local-first, end-to-end encrypted budgeting</p>
        </div>

        {/* Setup Card */}
        <div className="card bg-background-card/70 backdrop-blur-xl">
          {/* Progress Indicator */}
          <div className="flex items-center justify-center mb-8">
            <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold border ${
              step >= 1 ? 'bg-primary/15 text-primary border-primary/30' : 'bg-background-subtle text-text-muted border-border'
            }`}>
              1
            </div>
            <div className={`w-16 h-px mx-3 ${step >= 2 ? 'bg-primary/40' : 'bg-border'}`} />
            <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold border ${
              step >= 2 ? 'bg-primary/15 text-primary border-primary/30' : 'bg-background-subtle text-text-muted border-border'
            }`}>
              2
            </div>
          </div>

          {errors.general && (
            <div className="mb-6 p-4 rounded-xl border border-danger/20 bg-danger-bg flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-danger flex-shrink-0" />
              <p className="text-sm text-danger">{errors.general}</p>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {step === 1 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-semibold text-text-header">Create your account</h2>
                  <p className="mt-2 text-sm text-text-muted">
                    Choose a username for this device. Your data stays local and encrypted.
                  </p>
                </div>

                <div>
                  <label htmlFor="username" className="block text-sm font-medium text-text-muted mb-2">
                    Username
                  </label>
                  <input
                    type="text"
                    id="username"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    className={`input-field ${errors.username ? 'border-danger focus:border-danger focus:ring-danger' : ''}`}
                    placeholder="Enter your username"
                    autoComplete="username"
                    autoFocus
                  />
                  {errors.username && <p className="mt-2 text-sm text-danger">{errors.username}</p>}
                </div>

                  <button type="submit" className="btn btn-primary w-full shadow-glow">
                  Continue
                  <ArrowRight className="w-4 h-4" />
                </button>
                
                <div className="pt-2 text-center">
                  <button 
                    type="button" 
                    onClick={() => navigate('/login')} 
                    className="text-sm text-text-muted hover:text-primary transition-colors"
                  >
                    Already have an account? Sign in
                  </button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-semibold text-text-header">Set your password</h2>
                  <p className="mt-2 text-sm text-text-muted">
                    This password encrypts your data. Minimum 12 characters.
                  </p>
                </div>

                <PasswordInput
                  label="Password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Enter a strong password"
                  showStrength
                  error={errors.password}
                  autoComplete="new-password"
                  autoFocus
                />

                <PasswordInput
                  label="Confirm Password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  placeholder="Confirm your password"
                  error={errors.confirmPassword}
                  autoComplete="new-password"
                />

                <div className="p-4 rounded-xl border border-warning/20 bg-warning-bg flex items-start gap-3">
                  <KeyRound className="h-5 w-5 text-warning flex-shrink-0" />
                  <p className="text-sm text-text-body">
                    <span className="font-semibold text-text-header">Important:</span> This password cannot be recovered. If you forget it, the encrypted data cannot be decrypted.
                  </p>
                </div>

                <div className="flex gap-3">
                  <button type="button" onClick={handleBack} className="btn btn-secondary flex-1">
                    <ArrowLeft className="w-4 h-4" />
                    Back
                  </button>
                  <button type="submit" disabled={isSubmitting} className="btn btn-primary flex-1 shadow-glow">
                    {isSubmitting ? 'Creating…' : 'Create account'}
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>

        {/* Security Notice */}
        <div className="mt-6 text-center">
          <div className="inline-flex items-center text-sm text-text-muted">
            <Shield className="w-4 h-4 mr-2" />
            End-to-end encrypted (AES-256)
          </div>
        </div>
      </div>
    </div>
  );
}
